import React, { useState } from 'react'
import { useCalendar } from '../CalendarContext'
import type { CalendarEvent } from 'paintscout'
import moment from 'moment-timezone'
import { capitalize, makeStyles } from '@material-ui/core'
import {
  EstimatorAvatar,
  EventPreviewPopper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useClientOptions,
  useDialogs,
  useUsers
} from '@paintscout/ui'
import { useCancelEventFlow } from 'src/hooks'

import { EventDialog } from '../../../dialogs'
import { getTimezone, translateEventType } from '@paintscout/util/builder'

const useStyles = makeStyles((theme) => ({
  root: {
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    width: '100%'
  },
  timeCell: {
    width: '30%',
    verticalAlign: 'top',
    paddingTop: theme.spacing(1.25)
  },
  contentCell: {
    width: '55%'
  },
  actionButton: {
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  eventDetailsTitle: {
    fontSize: '16px',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& svg': {
      color: theme.palette.text.primary
    }
  }
}))

interface MobileEventTableProps {
  events: CalendarEvent[]
}

export default function MobileEventTable({ events }: MobileEventTableProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { updateEvent, deleteEvent } = useCalendar()
  const { users, userPictures } = useUsers()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()

  if (!events || events.length === 0) {
    return null
  }

  const handleOpenDrawer = (e: React.MouseEvent<HTMLTableRowElement>, event: CalendarEvent) => {
    if ((e.target as HTMLElement).classList?.contains('disable-click')) {
      return
    }
    setAnchorEl(e.currentTarget)
    setSelectedEvent(event)
  }

  const handleEditEvent = () => {
    openDialog(EventDialog, {
      event: selectedEvent,
      onDelete: (event: CalendarEvent, eventTemplate: string) => {
        deleteEvent(event, eventTemplate)
      },
      onConfirm: (event: CalendarEvent, eventTemplate: string) => {
        updateEvent(event, eventTemplate)
        dismissDialog()
      }
    })
    setAnchorEl(null)
    setSelectedEvent(null)
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      deleteEvent(evt, template)
      setAnchorEl(null)
    })

  const handleCloseDrawer = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setSelectedEvent(null)
    }, 300)
  }

  const getUser = (userId: string) => {
    return users.find((user) => user.user_id === userId)
  }

  const sortedEvents = [...events].sort((a, b) => {
    return moment(a.start).diff(moment(b.start))
  })

  const getEventTransparency = (event: CalendarEvent) => {
    return event.transparency === 'transparent' ? 'Free' : 'Busy'
  }
  return (
    <div
      className={classes.root}
      style={{
        overflowY: 'auto',
        maxHeight: '451px'
      }}
    >
      <Table>
        <TableBody>
          {sortedEvents.map((event, index) => (
            <TableRow key={index} onClick={(e) => handleOpenDrawer(e, event)}>
              <TableCell className={classes.timeCell}>
                <Typography>{moment(event.start).tz(timezone).format('h:mma')}</Typography>
              </TableCell>
              <TableCell className={classes.contentCell}>
                <Typography>
                  {event.eventType === '_google-calendar_'
                    ? 'Event'
                    : capitalize(translateEventType({ eventType: event.eventType, options }))}
                </Typography>
                <Typography variant="subtitle1">
                  {event.eventType === '_google-calendar_'
                    ? `Imported from Google Calendar (${getEventTransparency(event)})`
                    : event.contact?.firstName || event.contact?.lastName
                      ? [event.contact.firstName, event.contact.lastName].filter(Boolean).join(' ')
                      : '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <EstimatorAvatar
                  userPictures={userPictures}
                  owner={{
                    id: event.estimator.id,
                    firstName: getUser(event.estimator.id)?.user_metadata?.firstName,
                    lastName: getUser(event.estimator.id)?.user_metadata?.lastName
                  }}
                  borderColor={event.estimator.color}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <EventPreviewPopper
        event={selectedEvent}
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        users={users}
        onClose={handleCloseDrawer}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
      />
    </div>
  )
}
